import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, Outlet } from 'react-router-dom';
import { LogOut, RefreshCw } from 'lucide-react';

import LanguageSwitcher from 'shared/components/LanguageSwitcher';
import ConfirmModal from 'shared/components/ConfirmModal';
import { usePartnerAuth } from 'features/partner-auth';

import { PortalUIProvider, usePortalUI } from '../context/PortalUIContext';

const HAAT_LOGO_URL =
  'https://cdn.prod.website-files.com/6829acf870093a50cc6ceb69/682b03419ead6e45d8f2c59e_logo%20(2).svg';

const PortalLayout: React.FC = () => {
  return (
    <PortalUIProvider>
      <PortalShell />
    </PortalUIProvider>
  );
};

const PortalShell: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partner, logout } = usePartnerAuth();
  const { stickyTitle, onRefresh, isRefreshing } = usePortalUI();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await logout();
      navigate('/login', { replace: true });
    } finally {
      setLoggingOut(false);
      setLogoutOpen(false);
    }
  };

  const handleRefresh = () => {
    if (!onRefresh || isRefreshing) return;
    void onRefresh();
  };

  return (
    <div className="relative min-h-screen bg-[#f8f7f6]">
      <div
        aria-hidden="true"
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            'radial-gradient(900px 500px at 100% -5%, rgba(192,0,53,0.04) 0%, transparent 55%),' +
            'linear-gradient(180deg, #f8f7f6 0%, #f5f4f3 100%)',
        }}
      />

      <motion.header
        initial={{ y: -32, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/60"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/portal')}
            className="flex items-center gap-3"
          >
            <img src={HAAT_LOGO_URL} alt={t('brand.haat') ?? 'HAAT'} className="h-8 w-auto" />
          </button>

          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="header" />
            <button
              type="button"
              onClick={handleRefresh}
              disabled={!onRefresh || isRefreshing}
              aria-label={t('partnerPortal.nav.refresh') ?? 'Refresh'}
              aria-busy={isRefreshing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
              />
              <span className="hidden sm:inline">
                {t('partnerPortal.nav.refresh')}
              </span>
            </button>
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white border border-gray-200">
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-haat-red-500 to-haat-red-700 text-white text-xs font-bold flex items-center justify-center">
                {partner?.ownerName?.[0] ?? '?'}
              </div>
              <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">
                {partner?.ownerName}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setLogoutOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-red-50 hover:text-red-700 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t('partnerPortal.nav.logout')}</span>
            </button>
          </div>
        </div>

        {stickyTitle && (
          <div className="border-t border-gray-200/60">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-gradient-to-br from-haat-red-500 to-haat-red-700 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                {stickyTitle?.[0] ?? '?'}
              </div>
              <span className="truncate text-sm sm:text-base font-bold text-gray-900">
                {t('partnerPortal.hero.greeting', { name: stickyTitle })}
              </span>
            </div>
          </div>
        )}
      </motion.header>

      <main className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Outlet />
      </main>

      <ConfirmModal
        open={logoutOpen}
        title={t('partnerPortal.logoutConfirm.title') ?? ''}
        description={t('partnerPortal.logoutConfirm.message') ?? ''}
        confirmLabel={t('partnerPortal.logoutConfirm.confirm') ?? ''}
        cancelLabel={t('common.cancel') ?? ''}
        tone="danger"
        loading={loggingOut}
        icon={<LogOut className="h-5 w-5" />}
        onConfirm={handleLogout}
        onCancel={() => setLogoutOpen(false)}
      />
    </div>
  );
};

export default PortalLayout;
